import { useEffect, useState } from 'react';
import { signInWithPhoneNumber, signOut } from 'firebase/auth';
import logo from '../absolutesafetylogo.png';
import '../App.css';
import { auth, clearRecaptchaVerifier, createRecaptchaVerifier } from '../firebase';
import { isPortalUser } from '../utils/userRoles';

const RESEND_TIMEOUT_SECONDS = 90;
const RECAPTCHA_CONTAINER_ID = 'recaptcha-container';

// Helper function to convert Firebase error codes to user-friendly messages
const getFirebaseErrorMessage = (error) => {
  const errorCode = error?.code || '';
  const errorMessage = error?.message || '';
  
  // Map Firebase error codes to user-friendly messages
  const errorMessages = {
    'auth/invalid-verification-code': 'The verification code you entered is incorrect.',
    'auth/code-expired': 'The verification code has expired. Please request a new OTP.',
    'auth/invalid-verification-id': 'The verification session has expired. Please request a new OTP.',
    'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
    'auth/phone-number-already-exists': 'This phone number is already registered.',
    'auth/invalid-phone-number': 'Invalid mobile number. Please try again.',
    'auth/quota-exceeded': 'SMS quota exceeded. Please try again later.',
    'auth/captcha-check-failed': 'reCAPTCHA verification failed. Please try again.',
    'auth/app-not-authorized': 'App verification failed. Please try again.',
    // Phone auth / project config – show a clear message instead of technical Firebase text
    'auth/billing-not-enabled': 'Mobile number not registered.',
    'auth/operation-not-allowed': 'Mobile number not registered.',
    'auth/missing-phone-number': 'Invalid mobile number. Please try again.',
  };

  /** Avoid exposing raw "Firebase: Error (auth/...)" strings to users */
  const friendlyAuthFallback = (code) => {
    if (!code || typeof code !== 'string') return null;
    if (code.startsWith('auth/') && errorMessages[code]) return errorMessages[code];
    if (code.startsWith('auth/')) return 'Invalid mobile number. Please try again.';
    return null;
  };

  // Check if we have a specific message for this error code
  if (errorCode && errorMessages[errorCode]) {
    return errorMessages[errorCode];
  }

  // If the error message contains the code, extract and map it
  const codeMatch = errorMessage.match(/\(([^)]+)\)/);
  if (codeMatch && errorMessages[codeMatch[1]]) {
    return errorMessages[codeMatch[1]];
  }

  const fromParens = codeMatch?.[1];
  const fallbackFromParens = friendlyAuthFallback(fromParens);
  if (fallbackFromParens) return fallbackFromParens;

  const fallbackFromCode = friendlyAuthFallback(errorCode);
  if (fallbackFromCode) return fallbackFromCode;

  if (/auth\/billing-not-enabled|billing-not-enabled/i.test(errorMessage)) {
    return 'Mobile number not registered.';
  }

  // Return a generic message or the original message if no mapping found
  if (/^Firebase:\s*Error\s*\(auth\//i.test(errorMessage)) {
    return 'Invalid mobile number. Please try again.';
  }
  return errorMessage || 'An error occurred. Please try again.';
};

function LoginPage({ onLoginSuccess }) {
  const [countryCode, setCountryCode] = useState('+91');
  const [mobileNumber, setMobileNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [requestedPhoneNumber, setRequestedPhoneNumber] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);
  const [recaptchaKey, setRecaptchaKey] = useState(0);
  const [unauthorizedAccess, setUnauthorizedAccess] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_VERIFY_URL;

  useEffect(() => () => {
    clearRecaptchaVerifier();
  }, []);

  useEffect(() => {
    if (!resendCountdown) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      setResendCountdown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(intervalId);
  }, [resendCountdown]);

  const buildNormalizedPhoneNumber = (
    ccValue = countryCode,
    mobileValue = mobileNumber,
  ) => {
    const trimmedCode = (ccValue || '').replace(/\s+/g, '');
    const normalizedCode = trimmedCode
      ? trimmedCode.startsWith('+')
        ? trimmedCode
        : `+${trimmedCode}`
      : '';
    const normalizedNumber = (mobileValue || '').replace(/\D/g, '');
    return { normalizedCode, normalizedNumber, normalizedPhone: `${normalizedCode}${normalizedNumber}` };
  };

  const resetRecaptcha = () => {
    clearRecaptchaVerifier();
    setRecaptchaKey((prev) => prev + 1);
  };

  const resetToPhoneEntry = () => {
    resetRecaptcha();
    setConfirmationResult(null);
    setRequestedPhoneNumber('');
    setResendCountdown(0);
    setOtp('');
    setStatusMessage('');
    setError('');
  };

  const requestOtp = async (normalizedPhone, prefix = 'OTP sent to') => {
    try {
      setIsLoading(true);
      const appVerifier = createRecaptchaVerifier(RECAPTCHA_CONTAINER_ID);
      const result = await signInWithPhoneNumber(auth, normalizedPhone, appVerifier);
      setConfirmationResult(result);
      setRequestedPhoneNumber(normalizedPhone);
      setStatusMessage(`${prefix} ${normalizedPhone}`);
      setResendCountdown(RESEND_TIMEOUT_SECONDS);
      return result;
    } catch (err) {
      setError(getFirebaseErrorMessage(err));
      resetRecaptcha();
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOtp = async (event) => {
    event.preventDefault();
    setError('');
    setStatusMessage('');

    const { normalizedCode, normalizedNumber, normalizedPhone } = buildNormalizedPhoneNumber();
    if (!normalizedCode || normalizedNumber.length < 6) {
      setError('Please enter a valid country code and mobile number.');
      return;
    }

    try {
      await requestOtp(normalizedPhone);
    } catch {
      // errors handled in requestOtp
    }
  };

  const handleResendOtp = async () => {
    setError('');
    setStatusMessage('');

    if (!requestedPhoneNumber) {
      setError('Enter your mobile number and request an OTP first.');
      return;
    }

    try {
      await requestOtp(requestedPhoneNumber, 'OTP resent to');
    } catch {
      // errors handled in requestOtp
    }
  };

  const sendUidToBackend = async (user) => {
    if (!backendUrl) {
      setError('Backend URL is not configured. Please contact administrator.');
      return;
    }

    try {
      const response = await fetch(backendUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uid: user.uid,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to authenticate. Please retry.');
      }

      const data = await response.json();

      if (!isPortalUser(data.userDetails)) {
        try {
          await signOut(auth);
        } catch {
          /* ignore */
        }
        setUnauthorizedAccess(true);
        resetToPhoneEntry();
        return;
      }

      // Store last login time
      const now = new Date();
      localStorage.setItem('lastLoginTime', now.toISOString());

      if (onLoginSuccess) {
        onLoginSuccess({
          jwtToken: data.jwtToken,
          refreshToken: data.refreshToken,
          userDetails: data.userDetails,
          dashboardList: data.dashboardList,
        });
      }
    } catch (err) {
      setError(err.message || 'Failed to authenticate. Please retry.');
      throw err;
    }
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    setError('');
    setStatusMessage('');

    if (!confirmationResult) {
      setError('Please request an OTP first.');
      return;
    }

    if (otp.length < 4) {
      setError('Please enter the OTP sent to your phone.');
      return;
    }

    try {
      setIsLoading(true);
      const userCredential = await confirmationResult.confirm(otp);
      const { user } = userCredential;
      await sendUidToBackend(user);
    } catch (err) {
      setError(getFirebaseErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismissUnauthorized = () => {
    setUnauthorizedAccess(false);
  };

  if (unauthorizedAccess) {
    return (
      <div className="login-page">
        <div className="login-card login-unauthorized-card">
          <div className="brand">
            <img src={logo} alt="Absolute Safety logo" />
            <h1>Not authorized</h1>
            <p className="login-unauthorized-lead">You are not authorized to access this application.</p>
          </div>
          <div className="login-unauthorized-body">
            <p className="login-unauthorized-text">
              This portal is only available to <strong>Admin</strong> and <strong>Super Admin</strong> accounts.
              If you need access, please contact your administrator.
            </p>
            <button type="button" className="secondary-button login-unauthorized-button" onClick={handleDismissUnauthorized}>
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="brand">
          <img src={logo} alt="Absolute Safety logo" />
          <h1>Absolute Safety</h1>
          <p>Secure access with mobile OTP</p>
        </div>

        <form onSubmit={confirmationResult ? handleVerifyOtp : handleSendOtp}>
          {!confirmationResult ? (
            <>
              <div className="phone-row">
                <div className="input-group">
                  <label htmlFor="countryCode">Country code</label>
                  <input
                    id="countryCode"
                    type="tel"
                    placeholder="+91"
                    value={countryCode}
                    onChange={(event) => setCountryCode(event.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>
                <div className="input-group">
                  <label htmlFor="mobile">Mobile number</label>
                  <input
                    id="mobile"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    pattern="[0-9]*"
                    placeholder="9876543210"
                    value={mobileNumber}
                    onChange={(event) => setMobileNumber((event.target.value || '').replace(/\D/g, ''))}
                    disabled={isLoading}
                    required
                  />
                </div>
              </div>
              <small>OTP will be send to the mobile number.</small>
            </>
          ) : (
            <>
              <label htmlFor="otp">Enter OTP</label>
              <input
                id="otp"
                type="number"
                inputMode="numeric"
                placeholder="6-digit code"
                value={otp}
                onChange={(event) => setOtp(event.target.value)}
                disabled={isLoading}
                required
              />
              <small>Check your SMS for the one-time password.</small>
            </>
          )}

          {error && <p className="status error">{error}</p>}
          {statusMessage && <p className="status info">{statusMessage}</p>}

          {confirmationResult && (
            <div className="resend-row">
              <button
                type="button"
                className="link-button"
                onClick={resetToPhoneEntry}
                disabled={isLoading}
              >
                Use a different mobile number
              </button>
              <button
                type="button"
                className="secondary-button"
                onClick={handleResendOtp}
                disabled={isLoading || resendCountdown > 0}
              >
                {resendCountdown > 0
                  ? `Resend OTP in ${resendCountdown}s`
                  : 'Resend OTP'}
              </button>
              <small>
                {resendCountdown > 0
                  ? 'OTP can be resent after the timer finishes.'
                  : "Didn't get the code? Tap resend to receive a new OTP."}
              </small>
            </div>
          )}

          <button type="submit" disabled={isLoading}>
            {isLoading
              ? 'Please wait...'
              : confirmationResult
                ? 'Verify OTP'
                : 'Send OTP'}
          </button>
        </form>

        <div
          key={recaptchaKey}
          id="recaptcha-container"
          className={`recaptcha-container${confirmationResult ? ' recaptcha-hidden' : ''}`}
        />
      </div>
    </div>
  );
}

export default LoginPage;

